/**
 * Date adapter utilities using date-fns as a replacement for moment.js
 *
 * This module provides drop-in replacements for moment.js functionality,
 * maintaining similar APIs while using modern date-fns underneath.
 */

import {
  format,
  parse,
  parseISO,
  fromUnixTime,
  getUnixTime,
  addDays as fnsAddDays,
  addMonths as fnsAddMonths,
  addYears as fnsAddYears,
  differenceInYears,
  differenceInMonths,
  differenceInMilliseconds,
  differenceInDays,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfISOWeek,
  endOfDay,
  set,
  eachDayOfInterval,
  eachMonthOfInterval,
} from 'date-fns'

/**
 * Format a date using date-fns format string
 * Replaces: moment(date).format(formatString)
 *
 * @param date - Date to format (Date object, string, or number)
 * @param formatString - date-fns format string (e.g., 'yyyy-MM-dd')
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string | number,
  formatString: string
): string => {
  const dateObj =
    typeof date === 'string'
      ? parseISO(date)
      : typeof date === 'number'
        ? new Date(date)
        : date
  return format(dateObj, formatString)
}

/**
 * Parse a date string with a specific format
 * Replaces: moment(dateString, formatString)
 *
 * @param dateString - String to parse
 * @param formatString - Expected format (date-fns format)
 * @param strict - Whether to validate strictly (default: true)
 * @returns Parsed Date object
 */
export const parseDate = (
  dateString: string,
  formatString: string,
  strict = true
): Date => {
  return parse(dateString, formatString, new Date())
}

/**
 * Parse ISO 8601 date string
 * Replaces: moment(isoString)
 *
 * @param isoString - ISO 8601 date string
 * @returns Parsed Date object
 */
export const parseISODate = (isoString: string): Date => {
  return parseISO(isoString)
}

/**
 * Convert Unix timestamp (seconds) to Date
 * Replaces: moment.unix(timestamp) or moment(timestamp * 1000)
 *
 * @param timestamp - Unix timestamp in seconds
 * @returns Date object
 */
export const fromUnixTimestamp = (timestamp: number): Date => {
  return fromUnixTime(timestamp)
}

/**
 * Convert Date to Unix timestamp (seconds)
 * Replaces: moment().unix()
 *
 * @param date - Date to convert
 * @returns Unix timestamp in seconds
 */
export const toUnixTimestamp = (date: Date): number => {
  return getUnixTime(date)
}

/**
 * Add days to a date
 * Replaces: moment().add(amount, 'days')
 *
 * @param date - Starting date
 * @param amount - Number of days to add (can be negative)
 * @returns New Date object
 */
export const addDays = (date: Date, amount: number): Date => {
  return fnsAddDays(date, amount)
}

/**
 * Add months to a date
 * Replaces: moment().add(amount, 'months')
 *
 * @param date - Starting date
 * @param amount - Number of months to add (can be negative)
 * @returns New Date object
 */
export const addMonths = (date: Date, amount: number): Date => {
  return fnsAddMonths(date, amount)
}

/**
 * Add years to a date
 * Replaces: moment().add(amount, 'years')
 *
 * @param date - Starting date
 * @param amount - Number of years to add (can be negative)
 * @returns New Date object
 */
export const addYears = (date: Date, amount: number): Date => {
  return fnsAddYears(date, amount)
}

/**
 * Get difference between two dates in years
 * Replaces: moment(date2).diff(date1, 'years')
 *
 * @param date1 - Start date
 * @param date2 - End date
 * @returns Difference in years
 */
export const getDifferenceInYears = (date1: Date, date2: Date): number => {
  return differenceInYears(date2, date1)
}

/**
 * Get difference between two dates in months
 * Replaces: moment(date2).diff(date1, 'months')
 *
 * @param date1 - Start date
 * @param date2 - End date
 * @returns Difference in months
 */
export const getDifferenceInMonths = (date1: Date, date2: Date): number => {
  return differenceInMonths(date2, date1)
}

/**
 * Get difference between two dates in milliseconds
 * Replaces: moment(date2).diff(date1, 'milliseconds')
 *
 * @param date1 - Start date
 * @param date2 - End date
 * @returns Difference in milliseconds
 */
export const getDifferenceInMilliseconds = (
  date1: Date,
  date2: Date
): number => {
  return differenceInMilliseconds(date2, date1)
}

/**
 * Get difference between two dates in days
 * Replaces: moment(date2).diff(date1, 'days')
 *
 * @param date1 - Start date
 * @param date2 - End date
 * @returns Difference in days
 */
export const getDifferenceInDays = (date1: Date, date2: Date): number => {
  return differenceInDays(date2, date1)
}

/**
 * Check if date1 is after date2
 * Replaces: moment(date1).isAfter(date2)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if date1 is after date2
 */
export const isAfterDate = (date1: Date, date2: Date): boolean => {
  return isAfter(date1, date2)
}

/**
 * Check if date1 is before date2
 * Replaces: moment(date1).isBefore(date2)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if date1 is before date2
 */
export const isBeforeDate = (date1: Date, date2: Date): boolean => {
  return isBefore(date1, date2)
}

/**
 * Check if date1 is same or before date2
 * Replaces: moment(date1).isSameOrBefore(date2)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if date1 is same or before date2
 */
export const isSameOrBefore = (date1: Date, date2: Date): boolean => {
  return isBefore(date1, date2) || date1.getTime() === date2.getTime()
}

/**
 * Check if date1 is same or after date2
 * Replaces: moment(date1).isSameOrAfter(date2)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if date1 is same or after date2
 */
export const isSameOrAfter = (date1: Date, date2: Date): boolean => {
  return isAfter(date1, date2) || date1.getTime() === date2.getTime()
}

/**
 * Check if two dates are on the same day
 * Replaces: moment(date1).isSame(date2, 'day')
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if dates are on the same day
 */
export const isSameDayDate = (date1: Date, date2: Date): boolean => {
  return isSameDay(date1, date2)
}

/**
 * Get start of day (midnight)
 * Replaces: moment().startOf('day')
 *
 * @param date - Date to get start of
 * @returns Date at start of day (00:00:00)
 */
export const getStartOfDay = (date: Date): Date => {
  return startOfDay(date)
}

/**
 * Get end of day (23:59:59.999)
 * Replaces: moment().endOf('day')
 *
 * @param date - Date to get end of
 * @returns Date at end of day
 */
export const getEndOfDay = (date: Date): Date => {
  return endOfDay(date)
}

/**
 * Get start of ISO week (Monday)
 * Replaces: moment().startOf('isoWeek')
 *
 * @param date - Date to get start of week
 * @returns Date at start of ISO week
 */
export const getStartOfISOWeek = (date: Date): Date => {
  return startOfISOWeek(date)
}

/**
 * Set specific time components on a date
 * Replaces: moment().hours(h).minutes(m).seconds(s)
 *
 * @param date - Base date
 * @param values - Object with hours, minutes, seconds to set
 * @returns New Date with specified time
 */
export const setTime = (
  date: Date,
  values: {
    hours?: number
    minutes?: number
    seconds?: number
    milliseconds?: number
  }
): Date => {
  return set(date, values)
}

/**
 * Get array of dates for each day in a range
 * Replaces: Manual loop with moment().add(1, 'day')
 *
 * @param start - Start date
 * @param end - End date
 * @returns Array of Date objects
 */
export const getEachDayOfInterval = (start: Date, end: Date): Date[] => {
  return eachDayOfInterval({start, end})
}

/**
 * Get array of dates for each month in a range
 * Replaces: Manual loop with moment().add(1, 'month')
 *
 * @param start - Start date
 * @param end - End date
 * @returns Array of Date objects (first day of each month)
 */
export const getEachMonthOfInterval = (start: Date, end: Date): Date[] => {
  return eachMonthOfInterval({start, end})
}

/**
 * Convert moment format string to date-fns format string
 *
 * @param momentFormat - Moment.js format string
 * @returns date-fns format string
 */
export const convertMomentFormatToDateFns = (momentFormat: string): string => {
  return momentFormat
    .replace(/YYYY/g, 'yyyy')
    .replace(/DD/g, 'dd')
    .replace(/dddd/g, 'EEEE') // Full weekday name
    .replace(/ddd/g, 'EEE') // Short weekday name
    .replace(/A/g, 'a') // AM/PM
}

/**
 * Calculate duration between two dates as fractional years
 * Replaces: moment.duration(date2.diff(date1)).asYears()
 *
 * @param date1 - Start date
 * @param date2 - End date
 * @returns Difference as fractional years (e.g., 1.5 for 1 year 6 months)
 */
export const getDifferenceAsYears = (date1: Date, date2: Date): number => {
  const milliseconds = differenceInMilliseconds(date2, date1)
  // Average year length accounting for leap years
  const millisecondsPerYear = 365.25 * 24 * 60 * 60 * 1000
  return milliseconds / millisecondsPerYear
}
